@file:Suppress("INVISIBLE_MEMBER", "INVISIBLE_REFERENCE")

package cz.lhoracek.mapl.ui.localization

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.intl.Locale
import kotlinx.browser.window
import org.jetbrains.compose.resources.ComposeEnvironment
import org.jetbrains.compose.resources.DensityQualifier
import org.jetbrains.compose.resources.InternalResourceApi
import org.jetbrains.compose.resources.LanguageQualifier
import org.jetbrains.compose.resources.LocalComposeEnvironment
import org.jetbrains.compose.resources.RegionQualifier
import org.jetbrains.compose.resources.ResourceEnvironment
import org.jetbrains.compose.resources.ThemeQualifier

private const val LANGUAGE_OVERRIDE_KEY = "mapl.language.override"

actual fun isAppLanguageOverrideSupported(): Boolean = true

actual fun loadAppLanguageOverride(): String? =
    normalizeLanguageTag(window.localStorage.getItem(LANGUAGE_OVERRIDE_KEY))

actual fun saveAppLanguageOverride(languageTag: String?) {
    val normalized = normalizeLanguageTag(languageTag)
    if (normalized == null) {
        window.localStorage.removeItem(LANGUAGE_OVERRIDE_KEY)
    } else {
        window.localStorage.setItem(LANGUAGE_OVERRIDE_KEY, normalized)
    }
}

@Composable
actual fun ProvideAppLanguageOverride(
    languageTag: String?,
    content: @Composable () -> Unit,
) {
    val normalized = normalizeLanguageTag(languageTag)
    val composeEnvironment = remember(normalized) {
        OverriddenComposeEnvironment(normalized)
    }

    CompositionLocalProvider(
        LocalComposeEnvironment provides composeEnvironment,
        content = content,
    )
}

private class OverriddenComposeEnvironment(
    private val languageTag: String?,
) : ComposeEnvironment {
    @OptIn(InternalResourceApi::class)
    @Composable
    override fun rememberEnvironment(): ResourceEnvironment {
        val locale = languageTag?.let { remember(it) { Locale(it) } } ?: Locale.current
        val composeTheme = isSystemInDarkTheme()
        val composeDensity = LocalDensity.current

        return remember(locale, composeTheme, composeDensity) {
            ResourceEnvironment(
                language = LanguageQualifier(locale.language),
                region = RegionQualifier(locale.region),
                theme = ThemeQualifier.selectByValue(composeTheme),
                density = DensityQualifier.selectByDensity(composeDensity.density),
            )
        }
    }
}

private fun normalizeLanguageTag(languageTag: String?): String? {
    val value = languageTag?.trim()?.lowercase() ?: return null
    return when {
        value.startsWith("cs") -> "cs"
        value.startsWith("en") -> "en"
        else -> null
    }
}
